import 'App.scss';

import { MerchantFileUploadDocType } from 'APIs/files/files.types';
import { Industry } from 'APIs/industries/industries.types';
import { MerchantBusinessProfile, UpdateMerchantBusinessProfileRequest } from 'APIs/merchants/merchants.types';
import FileViewer from 'Components/Common/FileViewer';
import Message from 'Components/Message';
import Modal from 'Components/Modal/Modal';
import {
  countries,
  countryStatesRegions,
  merchantBusinessTypes,
  merchantCompanyTypes,
  merchantDocsAllowedFileTypes,
  merchantTaxCountries,
  merchantTimeZones,
} from 'Constants/Constants';
import React, { ChangeEvent, FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FaHourglassHalf } from 'react-icons/fa';
import { FiUpload } from 'react-icons/fi';
import { MdDelete } from 'react-icons/md';

import { yupResolver } from '@hookform/resolvers/yup';

import {
  Container,
  FileList,
  FormControl,
  FormItem,
  FormWrapper,
  Label,
  MultiColRow,
  SubTitle,
  VInput,
} from './styles';
import {
  MerchantBusinessProfileUpdateFormProps,
  toMerchantBusinessProfileUpdateFormProps,
  toUpdateMerchantBusinessProfileRequest,
} from './types';
import { validationSchema } from './validation.schema';

type Props = {
  merchantBusinessProfile: MerchantBusinessProfile | null;
  isLoading: boolean;
  errorMsg: string;
  industries: Array<Industry>;
  updateBusinessProfile: (data: UpdateMerchantBusinessProfileRequest) => Promise<void>;
  handleFileUploads: (file: File, docType: MerchantFileUploadDocType) => Promise<string | null>;
  downloadFile: (fileId: string) => Promise<{ fileUrl: string; fileType: string } | null>;
  setShowUpdateModal: (showUpdateModal: boolean) => void;
};

const BusinessProfileForm = ({
  merchantBusinessProfile,
  isLoading,
  errorMsg,
  industries,
  updateBusinessProfile,
  handleFileUploads,
  downloadFile,
  setShowUpdateModal,
}: Props) => {
  const UploadIcon = FiUpload as FunctionComponent;
  const RemoveFile = MdDelete as FunctionComponent;
  const HourGlassIcon = FaHourglassHalf as FunctionComponent;

  const [displayFileId, setDisplayFileId] = useState<string | null>(null);
  const [currentlyUploading, setCurrentlyUploading] = useState<Array<string>>([]);
  const [hideError, setHideError] = useState<boolean>(false);

  const errorContainerRef = useRef<HTMLDivElement>(null);

  const allowedFileTypesStr = useMemo(() => {
    return merchantDocsAllowedFileTypes.map((fT) => `.${fT}`).join(', ');
  }, []);

  const {
    register,
    handleSubmit,
    trigger,
    watch,
    getValues,
    setValue,
    setError,
    formState: { errors },
  } = useForm<MerchantBusinessProfileUpdateFormProps>({
    resolver: yupResolver(validationSchema),
    defaultValues: merchantBusinessProfile ? toMerchantBusinessProfileUpdateFormProps(merchantBusinessProfile) : {},
  });

  const onSubmit = useCallback(
    async (data) => {
      const reqData = toUpdateMerchantBusinessProfileRequest(data);
      updateBusinessProfile(reqData);
    },
    [updateBusinessProfile],
  );

  const handleFormSubmit = useCallback(() => {
    setValue('verificationDocs.partnershipAgreementFile', undefined);
    setValue('verificationDocs.trustDeedFile', undefined);
    setValue('verificationDocs.trustDeedAmendmentFile', undefined);
    return handleSubmit(onSubmit);
  }, [handleSubmit, onSubmit, setValue]);

  const fileUploadSelected = useCallback(
    async (event: ChangeEvent<HTMLInputElement>, dType: string, dKey: string, uploadDKey: string) => {
      event.preventDefault();
      const input = event.target;
      const file = input.files && input.files.length > 0 ? input.files[0] : null;

      if (!file) return;

      const dataKey = dKey as keyof MerchantBusinessProfileUpdateFormProps;
      const docType = dType as MerchantFileUploadDocType;
      const uploadDataKey = uploadDKey as keyof MerchantBusinessProfileUpdateFormProps;

      setValue(uploadDataKey, file as any);
      const isValid = await trigger(uploadDataKey);

      if (!isValid) return;

      setCurrentlyUploading((prev) => [...prev, dataKey]);
      const fileId = await handleFileUploads(file, docType);
      setCurrentlyUploading((prev) => prev.filter((dk) => dk !== dataKey));

      if (fileId) {
        const datavalue = getValues(dataKey);
        let value: any = fileId;
        if (
          dataKey === ('verificationDocs.trustDeedAmendmentFileIds' as keyof MerchantBusinessProfileUpdateFormProps)
        ) {
          if (datavalue instanceof Array) {
            value = [fileId, ...datavalue];
          } else {
            value = [fileId];
          }
        } else value = fileId;
        setValue(dataKey, value);
      } else {
        setError(uploadDataKey, {
          type: 'manual',
          message: 'File upload failed',
        });
      }
    },
    [getValues, handleFileUploads, setError, setValue, trigger],
  );

  const removeFile = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>, dataKey: string, fileId?: string) => {
      e.preventDefault();
      if (dataKey === 'verificationDocs.trustDeedAmendmentFileIds') {
        const fileIds = getValues(dataKey) as string[];
        if (fileIds instanceof Array && fileIds.length > 0) {
          setValue(
            'verificationDocs.trustDeedAmendmentFileIds',
            fileIds.length === 1 ? undefined : fileIds.filter((fId) => fId !== fileId),
          );
        }
      } else {
        setValue(dataKey as keyof MerchantBusinessProfileUpdateFormProps, undefined);
      }
    },
    [getValues, setValue],
  );

  const handleFileClick = (e: React.MouseEvent<HTMLButtonElement>, fileId: string) => {
    e.preventDefault();
    setDisplayFileId(fileId);
  };

  const handleFileModalClose = () => {
    setDisplayFileId(null);
  };

  const handleCancel = useCallback(() => {
    setShowUpdateModal(false);
  }, [setShowUpdateModal]);

  useEffect(() => {
    if (errorMsg && errorContainerRef && errorContainerRef.current) {
      setHideError(false);
      errorContainerRef.current.scrollIntoView();
    }
  }, [errorMsg]);

  const partnershipAgreementFileId = watch('verificationDocs.partnershipAgreementFileId');
  const trustDeedFileId = watch('verificationDocs.trustDeedFileId');
  const trustDeedAmendmentFileIds = watch('verificationDocs.trustDeedAmendmentFileIds');
  const addressCountry = watch('address.country');

  return (
    <Modal
      title="Edit business profile"
      cancelBtnText="Cancel"
      confirmBtnText="Save"
      handleCancel={handleCancel}
      handleSubmit={handleFormSubmit()}
      disableConfirmBtn={isLoading || currentlyUploading.length > 0}
      isLoading={isLoading}
      large={true}
      testId={'business-profile-update-modal'}
    >
      <Container>
        <FormWrapper>
          <div ref={errorContainerRef}>
            {errorMsg && !hideError && (
              <Message
                title="Failed to save merchant business profile"
                success={false}
                description={errorMsg}
                handleClose={() => setHideError(true)}
              ></Message>
            )}
          </div>
          <form>
            <FormItem>
              <Label>Business Name*</Label>
              <VInput {...register('businessName')} />
              {errors.businessName && <span className="error-msg">{errors.businessName.message}</span>}
            </FormItem>

            <MultiColRow>
              <FormItem className="col">
                <Label>Business Type*</Label>
                <FormControl {...register('businessType')}>
                  {merchantBusinessTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </FormControl>
                {errors.businessType && <span className="error-msg">{errors.businessType.message}</span>}
              </FormItem>
              <FormItem className="col">
                <Label>Company Type</Label>
                <FormControl {...register('companyType')}>
                  {[{ label: '-', value: '' }, ...merchantCompanyTypes].map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </FormControl>
                {errors.companyType && <span className="error-msg">{errors.companyType.message}</span>}
              </FormItem>
            </MultiColRow>

            <FormItem>
              <Label>Industry*</Label>
              <FormControl {...register('industry')}>
                {[...industries].map((industry) => (
                  <option key={industry.name} value={industry.name}>
                    {industry.description}
                  </option>
                ))}
              </FormControl>
              {errors.industry && <span className="error-msg">{errors.industry.message}</span>}
            </FormItem>

            <FormItem>
              <Label>Timezone*</Label>
              <FormControl {...register('timeZone')}>
                {merchantTimeZones.map((timezone) => (
                  <option key={timezone} value={timezone}>
                    {timezone}
                  </option>
                ))}
              </FormControl>
              {errors.timeZone && <span className="error-msg">{errors.timeZone.message}</span>}
            </FormItem>

            <MultiColRow>
              <FormItem className="col">
                <Label>Tax Country*</Label>
                <FormControl {...register('taxCountry')}>
                  {merchantTaxCountries.map((country) => (
                    <option key={country.value} value={country.value}>
                      {country.label}
                    </option>
                  ))}
                </FormControl>
                {errors.taxCountry && <span className="error-msg">{errors.taxCountry.message}</span>}
              </FormItem>
              <FormItem className="col">
                <Label>Tax ID*</Label>
                <VInput {...register('taxId')} />
                {errors.taxId && <span className="error-msg">{errors.taxId.message}</span>}
              </FormItem>
            </MultiColRow>

            <SubTitle>Branding details</SubTitle>

            <FormItem>
              <Label>Brand Name*</Label>
              <VInput {...register('brandName')} />
              {errors.brandName && <span className="error-msg">{errors.brandName.message}</span>}
            </FormItem>

            <FormItem>
              <Label>Website*</Label>
              <VInput {...register('website')} />
              {errors.website && <span className="error-msg">{errors.website.message}</span>}
            </FormItem>

            <MultiColRow>
              <FormItem className="col">
                <Label>Card Statement Name*</Label>
                <VInput {...register('cardStatementName')} />
                {errors.cardStatementName && <span className="error-msg">{errors.cardStatementName.message}</span>}
              </FormItem>
              <FormItem className="col">
                <Label>SMS Sender Name*</Label>
                <VInput {...register('smsSenderName')} />
                {errors.smsSenderName && <span className="error-msg">{errors.smsSenderName.message}</span>}
              </FormItem>
            </MultiColRow>

            <MultiColRow>
              <FormItem className="col">
                <Label>Support Site*</Label>
                <VInput {...register('supportSite')} />
                {errors.supportSite && <span className="error-msg">{errors.supportSite.message}</span>}
              </FormItem>
              <FormItem className="col">
                <Label>Support Email</Label>
                <VInput {...register('supportEmailAddress')} type="email" />
                {errors.supportEmailAddress && <span className="error-msg">{errors.supportEmailAddress.message}</span>}
              </FormItem>
            </MultiColRow>

            <SubTitle>Business contact details</SubTitle>

            <MultiColRow>
              <FormItem className="col">
                <Label>Given Name*</Label>
                <VInput {...register('contactPerson.givenName')} />
                {errors.contactPerson?.givenName && (
                  <span className="error-msg">{errors.contactPerson.givenName.message}</span>
                )}
              </FormItem>
              <FormItem className="col">
                <Label>Family Name*</Label>
                <VInput {...register('contactPerson.familyName')} />
                {errors.contactPerson?.familyName && (
                  <span className="error-msg">{errors.contactPerson.familyName.message}</span>
                )}
              </FormItem>
            </MultiColRow>

            <FormItem>
              <Label>Job title*</Label>
              <VInput {...register('contactPerson.title')} />
              {errors.contactPerson?.title && <span className="error-msg">{errors.contactPerson.title.message}</span>}
            </FormItem>

            <FormItem>
              <Label>Contact email*</Label>
              <VInput {...register('contactEmailAddress')} type="email" />
              {errors.contactEmailAddress && <span className="error-msg">{errors.contactEmailAddress.message}</span>}
            </FormItem>

            <FormItem>
              <Label>Phone Number*</Label>
              <VInput {...register('phoneNumber')} />
              {errors.phoneNumber && <span className="error-msg">{errors.phoneNumber.message}</span>}
            </FormItem>

            <SubTitle>Business address</SubTitle>

            <MultiColRow>
              <FormItem className="col">
                <Label>Unit Number</Label>
                <VInput {...register('address.unitNumber')} />
                {errors.address?.unitNumber && <span className="error-msg">{errors.address.unitNumber.message}</span>}
              </FormItem>
              <FormItem className="col">
                <Label>Street Number*</Label>
                <VInput {...register('address.streetNumber')} />
                {errors.address?.streetNumber && (
                  <span className="error-msg">{errors.address.streetNumber.message}</span>
                )}
              </FormItem>
            </MultiColRow>

            <MultiColRow>
              <FormItem>
                <Label>Street Name*</Label>
                <VInput {...register('address.streetName')} />
                {errors.address?.streetName && <span className="error-msg">{errors.address.streetName.message}</span>}
              </FormItem>
              <FormItem className="col">
                <Label>Street Type*</Label>
                <VInput {...register('address.streetType')} />
                {errors.address?.streetType && <span className="error-msg">{errors.address.streetType.message}</span>}
              </FormItem>
            </MultiColRow>

            <MultiColRow>
              <FormItem className="col">
                <Label>Suburb</Label>
                <VInput {...register('address.suburb')} />
                {errors.address?.suburb && <span className="error-msg">{errors.address.suburb.message}</span>}
              </FormItem>
              <FormItem className="col">
                <Label>Postal Code*</Label>
                <VInput {...register('address.postalCode')} type="number" />
                {errors.address?.postalCode && <span className="error-msg">{errors.address.postalCode.message}</span>}
              </FormItem>
            </MultiColRow>

            <MultiColRow>
              {['AU', 'US'].includes(addressCountry) ? (
                <FormItem className="col">
                  <Label>State*</Label>

                  <FormControl {...register('address.state')}>
                    <option value="">Select state</option>
                    {countryStatesRegions[addressCountry as keyof typeof countryStatesRegions].map((state) => (
                      <option key={state.value} value={state.value}>
                        {state.label}
                      </option>
                    ))}
                  </FormControl>
                  {errors.address?.state && <span className="error-msg">{errors.address.state.message}</span>}
                </FormItem>
              ) : (
                <FormItem className="col">
                  <Label>City*</Label>
                  {addressCountry === 'NZ' ? (
                    <FormControl {...register('address.city')}>
                      <option value="">Select city</option>
                      {countryStatesRegions.NZ.map((city) => (
                        <option key={city.value} value={city.value}>
                          {city.label}
                        </option>
                      ))}
                    </FormControl>
                  ) : (
                    <VInput {...register('address.city')} />
                  )}

                  {errors.address?.city && <span className="error-msg">{errors.address.city.message}</span>}
                </FormItem>
              )}

              <FormItem className="col">
                <Label>Country*</Label>
                <FormControl {...register('address.country')}>
                  {countries.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </FormControl>
                {errors.address?.country && <span className="error-msg">{errors.address.country.message}</span>}
              </FormItem>
            </MultiColRow>

            <SubTitle>Business verification documents</SubTitle>

            <MultiColRow>
              <FormItem className="col">
                <Label>Partnership agreement</Label>
                <span>
                  {partnershipAgreementFileId ? (
                    <FileList>
                      <li>
                        <button
                          type="button"
                          className="fileId"
                          onClick={(e) => handleFileClick(e, partnershipAgreementFileId)}
                        >
                          {partnershipAgreementFileId}
                        </button>
                        <button
                          type="button"
                          className="fileAction"
                          onClick={(e) => removeFile(e, 'verificationDocs.partnershipAgreementFileId')}
                        >
                          <RemoveFile />
                        </button>
                      </li>
                    </FileList>
                  ) : (
                    <span className="pending">-- Pending --</span>
                  )}
                </span>
                {errors.verificationDocs?.partnershipAgreementFileId && (
                  <div>
                    <span className="error-msg">{errors.verificationDocs.partnershipAgreementFileId.message}</span>
                  </div>
                )}
              </FormItem>
              <FormItem className="col">
                {currentlyUploading.includes('verificationDocs.partnershipAgreementFileId') ? (
                  <label className="uploadButton">
                    <HourGlassIcon />
                  </label>
                ) : (
                  <label className="uploadButton" htmlFor="verif-docs-partnership-agreement-upload">
                    <UploadIcon />
                  </label>
                )}
                <input
                  className="fileUpload"
                  id="verif-docs-partnership-agreement-upload"
                  type="file"
                  accept={allowedFileTypesStr}
                  {...(register('verificationDocs.partnershipAgreementFile'),
                  {
                    onChange: (e) =>
                      fileUploadSelected(
                        e,
                        'PartnershipAgreement',
                        'verificationDocs.partnershipAgreementFileId',
                        'verificationDocs.partnershipAgreementFile',
                      ),
                  })}
                />
                {errors.verificationDocs?.partnershipAgreementFile && (
                  <span className="error-msg">{errors.verificationDocs.partnershipAgreementFile.message}</span>
                )}
              </FormItem>
            </MultiColRow>

            <MultiColRow>
              <FormItem className="col">
                <Label>Trust deed</Label>
                <span>
                  {trustDeedFileId ? (
                    <FileList>
                      <li>
                        <button className="fileId" type="button" onClick={(e) => handleFileClick(e, trustDeedFileId)}>
                          {trustDeedFileId}
                        </button>
                        <button
                          type="button"
                          className="fileAction"
                          onClick={(e) => removeFile(e, 'verificationDocs.trustDeedFileId')}
                        >
                          <RemoveFile />
                        </button>
                      </li>
                    </FileList>
                  ) : (
                    <span className="pending">-- Pending --</span>
                  )}
                </span>
                {errors.verificationDocs?.trustDeedFileId && (
                  <div>
                    <span className="error-msg">{errors.verificationDocs.trustDeedFileId.message}</span>
                  </div>
                )}
              </FormItem>
              <FormItem className="col">
                {currentlyUploading.includes('verificationDocs.trustDeedFileId') ? (
                  <label className="uploadButton">
                    <HourGlassIcon />
                  </label>
                ) : (
                  <label className="uploadButton" htmlFor="verif-docs-trust-deeds-upload">
                    <UploadIcon />
                  </label>
                )}
                <input
                  className="fileUpload"
                  id="verif-docs-trust-deeds-upload"
                  type="file"
                  accept={allowedFileTypesStr}
                  {...(register('verificationDocs.trustDeedFile'),
                  {
                    onChange: (e) =>
                      fileUploadSelected(
                        e,
                        'TrustDeed',
                        'verificationDocs.trustDeedFileId',
                        'verificationDocs.trustDeedFile',
                      ),
                  })}
                />
                {errors.verificationDocs?.trustDeedFile && (
                  <span className="error-msg">{errors.verificationDocs.trustDeedFile.message}</span>
                )}
              </FormItem>
            </MultiColRow>

            <MultiColRow>
              <FormItem className="col">
                <Label>Trust deed amendments</Label>
                <span>
                  {trustDeedAmendmentFileIds ? (
                    <FileList>
                      {trustDeedAmendmentFileIds.map((fileId) => (
                        <li key={fileId}>
                          <button className="fileId" onClick={(e) => handleFileClick(e, fileId)}>
                            {fileId}
                          </button>
                          <button
                            type="button"
                            className="fileAction"
                            onClick={(e) => removeFile(e, 'verificationDocs.trustDeedAmendmentFileIds', fileId)}
                          >
                            <RemoveFile />
                          </button>
                        </li>
                      ))}
                    </FileList>
                  ) : (
                    <span className="pending">-- Pending --</span>
                  )}
                </span>
                {errors.verificationDocs?.trustDeedAmendmentFileIds && (
                  <div>
                    <span className="error-msg">{errors.verificationDocs.trustDeedAmendmentFileIds.message}</span>
                  </div>
                )}
              </FormItem>
              <FormItem className="col">
                {currentlyUploading.includes('verificationDocs.trustDeedAmendmentFileIds') ? (
                  <label className="uploadButton">
                    <HourGlassIcon />
                  </label>
                ) : (
                  <label className="uploadButton" htmlFor="verif-docs-trust-deeds-amend-upload">
                    <UploadIcon />
                  </label>
                )}

                <input
                  className="fileUpload"
                  id="verif-docs-trust-deeds-amend-upload"
                  type="file"
                  accept={allowedFileTypesStr}
                  {...(register('verificationDocs.trustDeedAmendmentFile'),
                  {
                    onChange: (e) =>
                      fileUploadSelected(
                        e,
                        'TrustDeedAmendment',
                        'verificationDocs.trustDeedAmendmentFileIds',
                        'verificationDocs.trustDeedAmendmentFile',
                      ),
                  })}
                />
                {errors.verificationDocs?.trustDeedAmendmentFile && (
                  <span className="error-msg">{errors.verificationDocs.trustDeedAmendmentFile.message}</span>
                )}
              </FormItem>
            </MultiColRow>
          </form>
        </FormWrapper>
        <FileViewer
          displayFileId={displayFileId}
          downloadFile={downloadFile}
          handleFileModalClose={handleFileModalClose}
        ></FileViewer>
      </Container>
    </Modal>
  );
};

export default BusinessProfileForm;
